import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DataIngestionService,
  MonthlyIndicatorResultDto,
} from 'src/generated-sources/openapi/data-ingestion/data-ingestion';
import {
  PlanIndicatorFindByPlanIdLightResponseDto,
  PlanIndicatorService,
  IndicatorCategoryDto,
} from 'src/generated-sources/openapi/configuration-service/plan-indicator';

import { formatDate, getMonthName, toLocalISOStringWithOffset } from '../../helpers/date-utils';
import { ChartConfigService, ChartData } from './chart-config.service';
import { IndicatorDataService } from 'src/app/services/data/indicator-data.service';

export interface IndicatorDto {
  id?: string;
  name?: string;
  dataFabricKey?: string;
}

export interface AttributeEvolutionState {
  indicators: IndicatorDto[];
  selectedIndicator: IndicatorDto | null;
  chartData: ChartData;
  isLoadingIndicators: boolean;
  isLoadingChart: boolean;
  hasSearched: boolean;
  error: string | null;
  lastProcessingDate: string;
}

@Injectable({ providedIn: 'any' })
export class AttributeEvolutionFacade {
  private readonly stateSubject = new BehaviorSubject<AttributeEvolutionState>({
    indicators: [],
    selectedIndicator: null,
    chartData: { labels: [], data: { values: [], label: '', type: '' } },
    isLoadingIndicators: false,
    isLoadingChart: false,
    hasSearched: false,
    error: null,
    lastProcessingDate: '',
  });

  constructor(
    private readonly dataIngestionService: DataIngestionService,
    private readonly planIndicatorService: PlanIndicatorService,
    private readonly chartConfigService: ChartConfigService,
    private readonly indicatorDataService: IndicatorDataService,
  ) {}

  get state$(): Observable<AttributeEvolutionState> {
    return this.stateSubject.asObservable();
  }

  get currentState(): AttributeEvolutionState {
    return this.stateSubject.value;
  }

  get lastProcessingDate$(): Observable<string> {
    return this.state$.pipe(map((state) => state.lastProcessingDate));
  }

  loadIndicators(planId: string): void {
    if (!planId) {
      this.updateState({ error: 'Plan ID is required to load indicators' });
      return;
    }

    this.updateState({ isLoadingIndicators: true, error: null });

    this.planIndicatorService.findByPlanIdLight({ planId }).subscribe({
      next: (response: PlanIndicatorFindByPlanIdLightResponseDto) => {
        const indicators: IndicatorDto[] = (response.items || [])
          .filter(
            (item) =>
              item.category === IndicatorCategoryDto.Quantity ||
              item.category === IndicatorCategoryDto.Value,
          )
          .map((item) => ({
            id: item.id,
            name: item.name,
            dataFabricKey: item.dataFabricKey,
          }));
        this.updateState({
          indicators: this.indicatorDataService.sortByDisplayOrder(
            indicators,
            (item) => item.id ?? '',
          ),
          selectedIndicator: null,
          isLoadingIndicators: false,
        });
      },
      error: (err: any) => {
        const errorMessage = $localize`:@@js_ErrorGettingIndicators:Error al recuperar indicadores`;
        this.updateState({
          error: errorMessage,
          isLoadingIndicators: false,
        });
      },
    });
  }

  loadChartData(registryId: string, indicatorId: string, startDate: Date, endDate: Date): void {
    this.updateState({ isLoadingChart: true, error: null });

    this.dataIngestionService
      .getLastMonthlyIndicatorValues({
        registryId,
        indicatorId,
        startDate: toLocalISOStringWithOffset(startDate),
        endDate: toLocalISOStringWithOffset(endDate),
      })
      .subscribe({
        next: (response: MonthlyIndicatorResultDto[]) => {
          const results = response || [];
          const chartData = this.processChartResults(results, startDate, endDate);
          const lastProcessingDate = this.getLastProcessingDate(results);
          this.updateState({
            chartData,
            lastProcessingDate,
            isLoadingChart: false,
            hasSearched: true,
          });
        },
        error: (err: any) => {
          const errorMessage = $localize`:@@js_ErrorGettingChartData:Error al recuperar datos del gráfico`;
          this.updateState({
            error: errorMessage,
            isLoadingChart: false,
          });
        },
      });
  }

  setSelectedIndicator(indicator: IndicatorDto | null): void {
    this.updateState({ selectedIndicator: indicator });
  }

  resetChartData(): void {
    this.updateState({
      chartData: this.chartConfigService.createEmptyChartData(),
      hasSearched: false,
      error: null,
    });
  }

  clearError(): void {
    this.updateState({ error: null });
  }

  private processChartResults(
    results: MonthlyIndicatorResultDto[],
    startDate: Date,
    endDate: Date,
  ): ChartData {
    const chartPoints = results.map((result) => ({
      ingestionMonth: result.ingestionMonth || '',
      value: result.value || '0',
    }));

    const validation = this.chartConfigService.validateChartData(chartPoints);
    if (!validation.isValid) {
      console.warn('Invalid chart data:', validation.error);
      return this.chartConfigService.createEmptyChartData();
    }

    let monthsRange: { date: Date; label: string }[] = [];
    if (startDate && endDate) {
      const range: { date: Date; label: string }[] = [];
      let d = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      while (d <= end) {
        const label = `${getMonthName(d.getMonth())} ${d.getFullYear()}`;
        range.push({ date: new Date(d), label });
        d.setMonth(d.getMonth() + 1);
      }
      monthsRange = range;
    }

    const selectedIndicator = this.currentState.selectedIndicator;
    return this.chartConfigService.processChartData(
      chartPoints,
      selectedIndicator?.name,
      monthsRange,
    );
  }

  private getLastProcessingDate(results: MonthlyIndicatorResultDto[]): string {
    if (results.length === 0) {
      return '';
    }

    const dates = results
      .map((result) => result.date)
      .filter((date) => date && date.trim() !== '')
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    if (dates.length === 0) {
      return '';
    }

    const lastDate = new Date(dates[0]);
    if (isNaN(lastDate.getTime())) {
      return '';
    }

    return formatDate(lastDate);
  }

  private updateState(changes: Partial<AttributeEvolutionState>): void {
    const currentState = this.currentState;
    this.stateSubject.next({ ...currentState, ...changes });
  }
}
